import { useMutation, useQueryCache } from "react-query";
import { useToasts } from "react-toast-notifications";
import { ToastStatuses } from "../../../components/global_components/Toast";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import { putNotifyNotificationContentMassNotification } from "../../../queries/notify";
import { buildNotifyInfiniteNotificationContentQueryKey } from "./useInfiniteNotificationContent";
import { buildNotifyContentDetailsQueryKey } from "./useNotifyContent";

export const ScheduleType = {
  Immediate: "immediate",
  Schedule: "schedule",
  Unschedule: "unschedule",
} as const;
export type ScheduleType = (typeof ScheduleType)[keyof typeof ScheduleType];

export function useNotifyNotificationContentScheduleMassNotificationAndArchive({
  onSuccess = () => {},
  onError = () => {},
}: {
  onSuccess?: () => void | Promise<void>;
  onError?: () => void;
} = {}) {
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const { addToast } = useToasts();

  const [scheduleMassNotificationAndArchiveMutation, { isLoading: isScheduleMassNotificationAndArchiveLoading }] =
    useMutation(putNotifyNotificationContentMassNotification(networkHeaders), {
      onSuccess: async (_, { id }) => {
        await queryCache.invalidateQueries(buildNotifyContentDetailsQueryKey({ id }));
        // Delay to hopefully allow search indexes to update
        setTimeout(async () => {
          await queryCache.invalidateQueries(buildNotifyInfiniteNotificationContentQueryKey());
        }, 1000);
        onSuccess();
      },
      onError: () => {
        addToast("Failed to create notify content.", { appearance: ToastStatuses.Error, autoDismiss: true });
        onError();
      },
    });

  const scheduleMassNotificationAndArchive = async ({
    contentId,
    sendAt,
    archiveAt,
  }: {
    contentId: number;
    sendAt?: {
      type: ScheduleType;
      date?: Date;
    };
    archiveAt?: {
      type: ScheduleType;
      date?: Date;
    };
  }) => {
    return await scheduleMassNotificationAndArchiveMutation({
      id: contentId,
      body: {
        sendAt: sendAt
          ? {
              type: sendAt.type,
              date: sendAt.date ? sendAt.date.toISOString() : undefined,
            }
          : undefined,
        archiveAt: archiveAt
          ? {
              type: archiveAt.type,
              date: archiveAt.date ? archiveAt.date.toISOString() : undefined,
            }
          : undefined,
      },
    });
  };

  return {
    scheduleMassNotificationAndArchive,
    isScheduleMassNotificationAndArchiveLoading,
  };
}
